import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { TextData } from '../dto/EntityRepository';
import { useTextViewStore } from '../store/TextViewStore';
import { useAuth } from './useApi/useAuth';
import { useTextListActions } from './useApi/useTextList';
import { useEditMode } from './useEditMode';
import { useUnsavedChanges } from './useUnsavedChanges';
import { useTextListMarqueeStore } from '../store/textListMarqueeStore';
import { useTextIndexStore } from '../store/TextIndexStore';
import { useTextIndexing } from './useTextIndexing';
import { isContiguousBlock, isCtrlOrMetaClick } from './workListSelectionUtils';

/** Texts 리스트: 편집 모드 다중 선택(Shift/Ctrl·Cmd 클릭, Shift+방향키), 일반 모드 방향키 이동 */
export function useTextListSelection(textList: TextData[]) {
  const { isOwner } = useAuth();
  const { isEditMode } = useEditMode();
  const { getText, getTextOwner } = useTextListActions();
  const { runAfterUnsavedChangesCheck } = useUnsavedChanges();
  const { isIndexingMode } = useTextIndexing();
  const setActiveText = useTextViewStore((s) => s.setActiveText);

  const [selectionAnchorId, setSelectionAnchorId] = useState('');
  const [shiftKeyboardRangeEndId, setShiftKeyboardRangeEndId] = useState<string | null>(null);
  const prevIsEditModeRef = useRef(isEditMode);

  useEffect(() => {
    if (selectionAnchorId && !textList.some((t) => t.id === selectionAnchorId)) {
      setSelectionAnchorId('');
    }
  }, [textList, selectionAnchorId]);

  useEffect(() => {
    if (shiftKeyboardRangeEndId && !textList.some((t) => t.id === shiftKeyboardRangeEndId)) {
      setShiftKeyboardRangeEndId(null);
    }
  }, [textList, shiftKeyboardRangeEndId]);

  useLayoutEffect(() => {
    const wasEditMode = prevIsEditModeRef.current;
    prevIsEditModeRef.current = isEditMode;
    if (!wasEditMode || isEditMode) return;

    const { multiSelectedText } = useTextViewStore.getState();
    if (multiSelectedText.length === 0) return;

    const first = multiSelectedText[0];
    const fromList = textList.find((t) => t.id === first.id) ?? first;

    setActiveText(fromList);
    setSelectionAnchorId(first.id);
    setShiftKeyboardRangeEndId(null);
    void (isOwner ? getTextOwner(first.id) : getText(first.id));
  }, [isEditMode, textList, isOwner, getText, getTextOwner, setActiveText]);

  const changeActiveText = (newId: string) => {
    const { setActiveText: setActive, setMultiSelectedText } = useTextViewStore.getState();
    const getTextWithDetailWithApi = async () => {
      const fromList = textList.find((t) => t.id === newId);
      if (fromList) {
        setActive(fromList);
      } else {
        setMultiSelectedText([]);
      }
      setSelectionAnchorId(newId);
      setShiftKeyboardRangeEndId(null);
      if (isOwner) {
        await getTextOwner(newId);
      } else {
        await getText(newId);
      }
    };
    const currentActiveId = useTextViewStore.getState().activeText?.id;
    if (newId !== currentActiveId) {
      runAfterUnsavedChangesCheck(() => {
        void getTextWithDetailWithApi();
      });
    }
  };

  const changeActiveTextRef = useRef(changeActiveText);
  changeActiveTextRef.current = changeActiveText;
  const textListRef = useRef(textList);
  textListRef.current = textList;

  const finalizeMarqueeHitTexts = useCallback(
    (texts: TextData[]) => {
      if (isIndexingMode) return;
      if (!isEditMode || texts.length === 0) return;

      if (texts.length === 1) {
        changeActiveTextRef.current(String(texts[0].id));
        return;
      }

      const store = useTextViewStore.getState();
      const run = () => {
        store.setMultiSelectedText(texts);
        setShiftKeyboardRangeEndId(null);
        setSelectionAnchorId(String(texts[0].id));
      };
      runAfterUnsavedChangesCheck(run);
    },
    [isEditMode, runAfterUnsavedChangesCheck, isIndexingMode]
  );

  const commitMarqueeHitTextsPreview = useCallback(() => {
    const { clearHitTexts } = useTextListMarqueeStore.getState();
    const snapshot = [...useTextListMarqueeStore.getState().hitTexts];
    if (isIndexingMode) {
      const { addSelectedIndexingText } = useTextIndexStore.getState();
      snapshot.forEach((t) => addSelectedIndexingText(t));
      clearHitTexts();
      return;
    }
    finalizeMarqueeHitTexts(snapshot);
    clearHitTexts();
  }, [finalizeMarqueeHitTexts, isIndexingMode]);

  const applyShiftRange = (endData: TextData) => {
    if (isIndexingMode) return;
    const store = useTextViewStore.getState();
    const at = store.activeText;
    const multiTexts = store.multiSelectedText;
    const list = textListRef.current;
    const anchor =
      (selectionAnchorId && list.some((t) => t.id === selectionAnchorId) ? selectionAnchorId : '') ||
      at?.id ||
      endData.id;

    const ai = list.findIndex((t) => t.id === anchor);
    const bi = list.findIndex((t) => t.id === endData.id);
    if (ai === -1 || bi === -1) {
      changeActiveTextRef.current(endData.id);
      return;
    }
    const lo = Math.min(ai, bi);
    const hi = Math.max(ai, bi);
    const rangeSlice = list.slice(lo, hi + 1);
    const rangeSet = new Set(rangeSlice.map((t) => t.id));
    const multiSet = new Set(multiTexts.map((t) => t.id));
    const mergedTexts =
      multiTexts.length > 0
        ? list.filter((t) => multiSet.has(t.id) || rangeSet.has(t.id))
        : rangeSlice;

    if (mergedTexts.length <= 1) {
      changeActiveTextRef.current(endData.id);
      return;
    }

    const mergedIds = mergedTexts.map((t) => t.id);
    const selectionUnchanged =
      multiTexts.length > 0 &&
      mergedIds.length === multiTexts.length &&
      mergedIds.every((id, i) => id === multiTexts[i].id);
    if (selectionUnchanged) {
      setShiftKeyboardRangeEndId(endData.id);
      return;
    }

    const run = () => {
      store.setMultiSelectedText(mergedTexts);
      setShiftKeyboardRangeEndId(endData.id);
    };
    runAfterUnsavedChangesCheck(run);
  };

  const applyShiftRangeRef = useRef(applyShiftRange);
  applyShiftRangeRef.current = applyShiftRange;

  const extendMultiSelectionByArrow = (direction: 'up' | 'down') => {
    if (isIndexingMode) return;
    const store = useTextViewStore.getState();
    const at = store.activeText;
    const multiTexts = store.multiSelectedText;
    const list = textListRef.current;

    const idSet = new Set<string>();
    if (multiTexts.length > 0) {
      multiTexts.forEach((t) => idSet.add(t.id));
    } else if (at) {
      idSet.add(at.id);
    } else {
      return;
    }

    const indices = list.map((t, i) => (idSet.has(t.id) ? i : -1)).filter((i) => i >= 0);
    if (indices.length === 0) return;
    const minIdx = Math.min(...indices);
    const maxIdx = Math.max(...indices);
    const contiguous = isContiguousBlock(minIdx, maxIdx, idSet, list);

    const applyMulti = (mergedIds: string[], focusId: string) => {
      if (mergedIds.length <= 1) {
        if (mergedIds.length === 1) changeActiveTextRef.current(mergedIds[0]);
        return;
      }
      const mergedIdSet = new Set(mergedIds);
      const mergedTextsList = list.filter((t) => mergedIdSet.has(t.id));
      const run = () => {
        store.setMultiSelectedText(mergedTextsList);
        setShiftKeyboardRangeEndId(focusId);
      };
      runAfterUnsavedChangesCheck(run);
    };

    if (direction === 'down') {
      if (maxIdx >= list.length - 1) return;
      const newId = list[maxIdx + 1].id;
      const nextSet = new Set(idSet);
      nextSet.add(newId);
      const mergedIds = list.filter((t) => nextSet.has(t.id)).map((t) => t.id);
      applyMulti(mergedIds, newId);
      return;
    }

    if (contiguous) {
      if (minIdx > 0) {
        const mergedIds = list.slice(minIdx - 1, maxIdx + 1).map((t) => t.id);
        applyMulti(mergedIds, list[minIdx - 1].id);
      } else {
        if (maxIdx <= minIdx) return;
        const mergedIds = list.slice(minIdx, maxIdx).map((t) => t.id);
        if (mergedIds.length === 1) {
          changeActiveTextRef.current(mergedIds[0]);
          return;
        }
        applyMulti(mergedIds, list[maxIdx - 1].id);
      }
      return;
    }

    const prevIdx = maxIdx - 1;
    if (prevIdx >= minIdx && !idSet.has(list[prevIdx].id)) {
      const mergedIds = list.slice(minIdx, maxIdx).map((t) => t.id);
      if (mergedIds.length === 1) {
        changeActiveTextRef.current(mergedIds[0]);
        return;
      }
      applyMulti(mergedIds, list[maxIdx - 1].id);
      return;
    }

    const maxId = list[maxIdx].id;
    idSet.delete(maxId);
    const mergedIds = list.filter((t) => idSet.has(t.id)).map((t) => t.id);
    if (mergedIds.length === 0) {
      runAfterUnsavedChangesCheck(() => {
        store.setMultiSelectedText([]);
        setSelectionAnchorId('');
        setShiftKeyboardRangeEndId(null);
      });
      return;
    }
    if (mergedIds.length === 1) {
      changeActiveTextRef.current(mergedIds[0]);
      return;
    }
    const remaxIdx = Math.max(
      ...mergedIds.map((id) => list.findIndex((t) => t.id === id)).filter((i) => i >= 0)
    );
    applyMulti(mergedIds, list[remaxIdx].id);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (isIndexingMode) return;
    const list = textListRef.current;
    if (!list.length) return;

    if (isEditMode && e.shiftKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      extendMultiSelectionByArrow(e.key === 'ArrowDown' ? 'down' : 'up');
      return;
    }

    if (isEditMode) return;
    const activeTextId = useTextViewStore.getState().activeText?.id ?? '';
    const activeIndex = list.findIndex((each) => each.id === activeTextId);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const newIndex = activeIndex + 1;
      if (newIndex < list.length) {
        const newId = list[newIndex].id;
        changeActiveTextRef.current(newId);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const newIndex = activeIndex - 1;
      if (newIndex >= 0) {
        const newId = list[newIndex].id;
        changeActiveTextRef.current(newId);
      }
    }
  };

  const handleTextClick = useCallback((data: TextData, e: MouseEvent) => {
    if (isIndexingMode) return;
    if (e.shiftKey && isEditMode) {
      applyShiftRangeRef.current(data);
      return;
    }

    const ctrl = isEditMode && isCtrlOrMetaClick(e);
    const store = useTextViewStore.getState();
    const { activeText: at, multiSelectedText: multiTexts } = store;
    const multiEmpty = multiTexts.length === 0;
    const inMulti = multiTexts.some((t) => t.id === data.id);

    if (!multiEmpty) {
      if (!inMulti) {
        if (ctrl) {
          runAfterUnsavedChangesCheck(() => {
            store.addMultiSelectedText([data]);
            setSelectionAnchorId(data.id);
            setShiftKeyboardRangeEndId(data.id);
          });
          return;
        }
        changeActiveTextRef.current(data.id);
        return;
      }
      const next = multiTexts.filter((t) => t.id !== data.id);
      if (next.length === 0) {
        runAfterUnsavedChangesCheck(() => {
          store.setMultiSelectedText([]);
          setSelectionAnchorId('');
          setShiftKeyboardRangeEndId(null);
        });
        return;
      }
      if (next.length === 1) {
        const [only] = next;
        changeActiveTextRef.current(only.id);
        return;
      }
      runAfterUnsavedChangesCheck(() => {
        store.setMultiSelectedText(next);
        setShiftKeyboardRangeEndId(null);
      });
      return;
    }

    if (!at) {
      changeActiveTextRef.current(data.id);
      return;
    }

    if (!ctrl) {
      changeActiveTextRef.current(data.id);
      return;
    }

    if (at.id === data.id) return;

    const prevActive = at;
    const prevFromList =
      textListRef.current.find((text) => text.id === prevActive.id) ?? prevActive;
    const enterMulti = () => {
      setSelectionAnchorId(prevActive.id);
      setShiftKeyboardRangeEndId(data.id);
      store.setMultiSelectedText([prevFromList, data]);
    };
    runAfterUnsavedChangesCheck(enterMulti);
  }, [isIndexingMode, isEditMode, runAfterUnsavedChangesCheck]);

  const handleTextListBackgroundClick = (e: MouseEvent<HTMLElement>) => {
    if (isIndexingMode) return;
    const t = e.target as HTMLElement;
    if (t.closest('[data-text-id]')) return;
    if (t.closest('[data-suppress-marquee]')) return;
    if (!isEditMode) return;
    const multi = useTextViewStore.getState().multiSelectedText;
    if (multi.length < 2) return;

    const first = multi[0];
    const newId = String(first.id);
    const fromList = textListRef.current.find((t) => t.id === first.id) ?? first;

    const collapseMultiToFirstActive = async () => {
      const { setActiveText: setActive, setMultiSelectedText } = useTextViewStore.getState();
      setActive(fromList);
      setMultiSelectedText([]);
      if (isOwner) {
        await getTextOwner(newId);
      } else {
        await getText(newId);
      }
    };

    setSelectionAnchorId(newId);
    setShiftKeyboardRangeEndId(null);
    runAfterUnsavedChangesCheck(() => {
      void collapseMultiToFirstActive();
    });
  };

  return {
    handleTextClick,
    handleKeyDown,
    commitMarqueeHitTextsPreview,
    handleTextListBackgroundClick,
  };
}
